import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Square, Trash2, Download, ArrowRight, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const KEY = 'ivoliq_free_timer_v1';

const load = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || { running: null, entries: [] }; } catch { return { running: null, entries: [] }; }
};
const save = (state) => localStorage.setItem(KEY, JSON.stringify(state));
const pad = (n) => String(n).padStart(2, '0');
const fmtDur = (sec) => `${pad(Math.floor(sec / 3600))}:${pad(Math.floor((sec % 3600) / 60))}:${pad(sec % 60)}`;
const fmtHours = (sec) => `${(sec / 3600).toFixed(2).replace('.', ',')} h`;

export function FreeTimer({ onSignupClick }) {
  const [state, setState] = useState(load);
  const [project, setProject] = useState('');
  const [now, setNow] = useState(Date.now());
  const tick = useRef(null);

  useEffect(() => { save(state); }, [state]);
  useEffect(() => {
    if (state.running) {
      tick.current = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(tick.current);
    }
    return undefined;
  }, [state.running]);

  const elapsed = state.running ? Math.floor((now - state.running.start) / 1000) : 0;
  const today = new Date().toDateString();
  const todaySec = state.entries.filter(e => new Date(e.start).toDateString() === today).reduce((a, e) => a + e.seconds, 0) + elapsed;

  const start = () => setState(s => ({ ...s, running: { project: project.trim() || 'Ohne Projekt', start: Date.now() } }));
  const stop = () => setState(s => {
    if (!s.running) return s;
    const seconds = Math.max(1, Math.floor((Date.now() - s.running.start) / 1000));
    return { running: null, entries: [{ id: Date.now(), project: s.running.project, start: s.running.start, seconds }, ...s.entries].slice(0, 50) };
  });
  const remove = (id) => setState(s => ({ ...s, entries: s.entries.filter(e => e.id !== id) }));
  const exportCsv = () => {
    const rows = [['Datum', 'Projekt', 'Start', 'Dauer (h)'], ...state.entries.map(e => [new Date(e.start).toLocaleDateString('de-DE'), e.project, new Date(e.start).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }), (e.seconds / 3600).toFixed(2)])];
    const blob = new Blob([rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'zeiterfassung-ivoliq.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-xl ring-1 ring-black/5" data-testid="free-timer">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">Kostenloser Timer · ohne Anmeldung</p>
        <span className="text-xs text-gray-500" data-testid="free-timer-today">Heute: <strong className="text-gray-900">{fmtHours(todaySec)}</strong></span>
      </div>

      <div className={`mt-4 rounded-xl p-5 text-white ${state.running ? 'bg-[#062417] seo-grain' : 'bg-gray-900'}`}>
        <p className="font-mono text-4xl sm:text-5xl font-bold tabular-nums" data-testid="free-timer-display">{fmtDur(elapsed)}</p>
        <p className="mt-1 text-sm text-emerald-200/80">{state.running ? `Läuft: ${state.running.project}` : 'Bereit – Projekt eintragen und starten'}</p>
      </div>

      {state.running ? (
        <Button onClick={stop} className="mt-4 w-full h-12 bg-red-600 hover:bg-red-700 text-white text-base font-semibold" data-testid="free-timer-stop"><Square className="h-4 w-4 mr-2" /> Stoppen & speichern</Button>
      ) : (
        <div className="mt-4 flex gap-2">
          <Input value={project} onChange={(e) => setProject(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && start()} placeholder="Projekt oder Kunde, z. B. Website Relaunch" className="h-12 text-base" data-testid="free-timer-project" />
          <Button onClick={start} className="h-12 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="free-timer-start"><Play className="h-4 w-4 mr-1.5" /> Start</Button>
        </div>
      )}

      {state.entries.length > 0 && (
        <ul className="mt-5 divide-y divide-gray-100 text-sm" data-testid="free-timer-entries">
          {state.entries.slice(0, 5).map(e => (
            <li key={e.id} className="flex items-center justify-between gap-3 py-2">
              <span className="flex items-center gap-2 min-w-0"><Clock className="h-3.5 w-3.5 flex-none text-gray-400" /><span className="truncate text-gray-800">{e.project}</span><span className="text-xs text-gray-400">{new Date(e.start).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</span></span>
              <span className="flex items-center gap-3"><strong className="tabular-nums text-gray-900">{fmtHours(e.seconds)}</strong><button type="button" onClick={() => remove(e.id)} aria-label="Eintrag löschen" className="text-gray-400 hover:text-red-600" data-testid={`free-timer-delete-${e.id}`}><Trash2 className="h-4 w-4" /></button></span>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={exportCsv} disabled={!state.entries.length} className="h-11" data-testid="free-timer-export"><Download className="h-4 w-4 mr-2" /> CSV exportieren</Button>
        <Link to="/signup" onClick={() => { try { localStorage.setItem('post_auth_redirect', '/time-tracking'); } catch { /* ignore */ } onSignupClick?.(); }} className="flex-1" data-testid="free-timer-signup"><Button className="w-full h-11 bg-[#062417] hover:bg-emerald-900 text-white font-semibold">Zeiten in ivoliq übernehmen <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
      </div>
      <p className="mt-3 text-[11px] text-gray-500">Einträge bleiben nur in diesem Browser gespeichert. Legst du ein kostenloses ivoliq-Konto an, werden sie automatisch übernommen – und daraus Projekte, Rechnungen und Steuer-Rücklage.</p>
    </div>
  );
}

export default FreeTimer;
